import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Container,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
  Alert,
  Paper,
  Grid,
  MenuItem
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isSunday, isWednesday, isAfter, format, setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns';
import countries from '../data/countries';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization, getOrganizationId } from '../utils/organizationContext';
import useOrganizationName from '../hooks/useOrganizationName';
import FormOrganizationBanner from './FormOrganizationBanner';
import axios from 'axios';

const FirstTimerForm = () => {
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm({
    defaultValues: {
      country: 'Canada',
      marital_status: '',
      campus_location: '',
      campus_duration: ''
    }
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [serviceDate, setServiceDate] = useState(new Date());
  const [formData, setFormData] = useState({
    service_date: null,
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'Canada',  // Set default country
    gender: '',
    heard_about_us: '',
    prayer_request: '',
    is_visiting_member: false,
    is_student: false,
    school: ''
  });
  const [submitError, setSubmitError] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const { organizationSlug } = useAuth();
  const { organizationSlug: urlSlug } = useParams();
  const [currentOrgSlug, setCurrentOrgSlug] = useState(null);
  const { organizationName } = useOrganizationName();
  
  const isStudent = watch('is_student', false);
  const navigate = useNavigate();

  // Get organization from URL
  useEffect(() => {
    const slug = urlSlug || organizationSlug || getCurrentOrganization();
    setCurrentOrgSlug(slug);
    
    if (!slug && !organizationSlug && window.location.pathname === '/add-first-timer') {
      window.location.href = '/';
    }
  }, [urlSlug, organizationSlug]);

  // Function to check for duplicates
  const checkForDuplicate = async (data) => {
    try {
      setIsCheckingDuplicate(true);
      setDuplicateInfo(null);
      
      const normalizedDate = setMilliseconds(
        setSeconds(
          setMinutes(
            setHours(serviceDate, 12),
            0),
          0),
        0
      );

      const checkData = {
        email: data.email || '',
        phone_number: data.phone_number || '',
        service_date: format(normalizedDate, 'yyyy-MM-dd'),
        ...(currentOrgSlug && { organization_slug: currentOrgSlug })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/first-timer/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.isDuplicate) {
          setDuplicateInfo(result.duplicate);
          return true; // Duplicate found
        }
      }
      return false; // No duplicate
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false; // Continue with submission if check fails
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSubmitError('');
      setDuplicateInfo(null);
      
      // First check for duplicates
      const isDuplicate = await checkForDuplicate(data);
      if (isDuplicate) {
        return; // Stop submission if duplicate found
      }

      const normalizedDate = setMilliseconds(
        setSeconds(
          setMinutes(
            setHours(serviceDate, 12),
            0),
          0),
        0
      );

      // Convert string "true"/"false" to actual boolean values
      const formattedData = {
        ...data,
        service_date: format(normalizedDate, 'yyyy-MM-dd'),
        is_visiting_member: data.is_visiting_member === 'true',
        is_student: data.is_student === 'true',
        needs_transportation: data.needs_transportation === 'true',
        is_relocating_member: data.is_relocating_member === 'true',
        wants_wcito_membership: data.wants_wcito_membership === 'true',
        given_life_to_christ: data.given_life_to_christ === 'true',
        water_baptized: data.water_baptized === 'true',
        holy_ghost_baptized: data.holy_ghost_baptized === 'true',
        ...(currentOrgSlug && { organization_slug: currentOrgSlug })
      };

      console.log('Submitting data:', formattedData);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/first-timer/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle duplicate error from backend
        if (response.status === 409) {
          setDuplicateInfo(errorData.duplicate);
          setSubmitError(errorData.message || 'You have already registered for this service date.');
          return;
        }
        
        throw new Error(errorData.error || 'Failed to submit form');
      }

      const result = await response.json();
      // Navigate to organization-prefixed success page
      const successPath = currentOrgSlug ? `/${currentOrgSlug}/submission-success` : '/submission-success';
      navigate(successPath);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Failed to submit form. Please try again.');
    }
  };

  const validateServiceDate = (date) => {
    if (!date) return false;
    if (isAfter(date, new Date())) return false;
    return isSunday(date) || isWednesday(date);
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Welcome to {organizationName}
        </Typography>
        <FormOrganizationBanner subtitle="First timer registration for this church branch" />
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{
            mb: 4,
            fontSize: '1.1rem',
            lineHeight: 1.6,
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          We're glad to have you! Please share your details so we can get to know you better.
        </Typography>
        
        {submitStatus.message && (
          <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
            {submitStatus.message}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {duplicateInfo && (
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Duplicate Registration Detected
            </Typography>
            <Typography variant="body2">
              A registration already exists for <strong>{duplicateInfo.name}</strong> on <strong>{duplicateInfo.service_date}</strong>.
            </Typography>
            {duplicateInfo.email && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Email: {duplicateInfo.email}
              </Typography>
            )}
            {duplicateInfo.phone && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Phone: {duplicateInfo.phone}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              If this is a different person, please contact the church office for assistance.
            </Typography>
          </Alert>
        )}

        {isCheckingDuplicate && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Checking for existing registrations...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Service Date"
              value={serviceDate}
              onChange={(newValue) => setServiceDate(newValue)}
              fullWidth
              required
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            margin="normal"
            label="First Name"
            required
            {...register('first_name', { required: 'First name is required' })}
            error={!!errors.first_name}
            helperText={errors.first_name?.message}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Last Name"
            required
            {...register('last_name', { required: 'Last name is required' })}
            error={!!errors.last_name}
            helperText={errors.last_name?.message}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            required
            {...register('email', { required: 'Email is required' })}
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Phone Number"
            required
            {...register('phone_number', { required: 'Phone number is required' })}
            error={!!errors.phone_number}
            helperText={errors.phone_number?.message}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Address Line 1"
            {...register('address_line1')}
            inputProps={{ maxLength: 256 }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Address Line 2"
            {...register('address_line2')}
            inputProps={{ maxLength: 256 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                {...register('city')}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Postal Code"
                {...register('postal_code')}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>

          <TextField
            select
            label="Country"
            {...register('country')}
            fullWidth
            margin="normal"
            defaultValue="Canada"
          >
            {countries.map((country) => (
              <MenuItem key={country.code} value={country.name}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>

          <FormControl component="fieldset" margin="normal">
            <FormLabel>Gender</FormLabel>
            <RadioGroup row value={watch('gender') || ''}>
              <FormControlLabel
                value="male"
                control={<Radio {...register('gender')} />}
                label="Male"
              />
              <FormControlLabel
                value="female"
                control={<Radio {...register('gender')} />}
                label="Female"
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel>Marital Status</FormLabel>
            <RadioGroup row>
              <FormControlLabel
                value="single"
                control={<Radio {...register('marital_status')} />}
                label="Single"
              />
              <FormControlLabel
                value="married"
                control={<Radio {...register('marital_status')} />}
                label="Married"
              />
              <FormControlLabel
                value="divorced"
                control={<Radio {...register('marital_status')} />}
                label="Divorced"
              />
              <FormControlLabel
                value="widowed"
                control={<Radio {...register('marital_status')} />}
                label="Widowed"
              />
            </RadioGroup>
          </FormControl>

          {['is_relocating_member', 'wants_wcito_membership', 'given_life_to_christ', 
            'water_baptized', 'holy_ghost_baptized'].map((field) => (
            <Box sx={{ mt: 2 }} key={field}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel>
                  {field === 'is_relocating_member' && 'Are you a relocating Winners member?'}
                  {field === 'wants_wcito_membership' && `Would you like to be a member of ${organizationName}?`}
                  {field === 'given_life_to_christ' && 'Have you given your life to Jesus Christ?'}
                  {field === 'water_baptized' && 'Have you been Baptized in water by immersion?'}
                  {field === 'holy_ghost_baptized' && 'Have you been Baptized in the Holy Ghost with evidence of speaking in Tongues?'}
                </FormLabel>
                <RadioGroup 
                  row
                  value={watch(field) || ''}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio {...register(field)} />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio {...register(field)} />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            <FormControl 
              component="fieldset"
              error={!!errors.needs_transportation}
            >
              <FormLabel>Would you require transportation support to come to church?</FormLabel>
              <RadioGroup 
                row 
                value={watch('needs_transportation') || ''}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio {...register('needs_transportation')} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio {...register('needs_transportation')} />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    {...register('is_visiting_member')}
                  />
                }
                label="Are you a visiting member?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    {...register('is_student')}
                  />
                }
                label="Are you a student?"
              />
            </Grid>
          </Grid>

          {isStudent && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="School Name"
                {...register('school')}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Campus Location"
                {...register('campus_location')}
              />

              <FormControl component="fieldset" margin="normal" fullWidth>
                <FormLabel>Campus Duration</FormLabel>
                <RadioGroup row>
                  <FormControlLabel
                    value="1_year"
                    control={<Radio {...register('campus_duration')} />}
                    label="1 Year"
                  />
                  <FormControlLabel
                    value="2_years"
                    control={<Radio {...register('campus_duration')} />}
                    label="2 Years"
                  />
                  <FormControlLabel
                    value="2plus_years"
                    control={<Radio {...register('campus_duration')} />}
                    label="2+ Years"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="How did you hear about us?"
            {...register('heard_about_us')}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Prayer Request"
            multiline
            rows={3}
            {...register('prayer_request')}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isCheckingDuplicate}
            sx={{ mt: 3 }}
          >
            {isCheckingDuplicate ? 'Checking...' : 'Submit'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default FirstTimerForm; 